import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileText, Upload, Trash2, Download, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const DocumentsManagement = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    const token = localStorage.getItem("token");
    let success = 0;

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/documents/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        success++;
      } catch (error) {
        toast.error(`Villa: ${file.name} - ${error.response?.data?.detail || "Upload mistókst"}`);
      }
    }

    if (success > 0) {
      toast.success(`${success} ${success === 1 ? "skjal" : "skjöl"} hlaðið upp!`);
      fetchDocuments();
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eyða ${name}?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/documents/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Skjal eytt");
      fetchDocuments();
    } catch (error) {
      toast.error("Villa við að eyða skjali");
    }
  };

  const getFileIcon = (name) => {
    const ext = name.split(".").pop().toLowerCase();
    const colors = { pdf: "text-red-500", doc: "text-blue-500", docx: "text-blue-500", xls: "text-green-500", xlsx: "text-green-500" };
    return <FileText className={`w-5 h-5 ${colors[ext] || "text-neutral-500"}`} />;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="flex items-center gap-3 mb-8">
          <FileText className="w-8 h-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight" data-testid="documents-title">
            Skjöl
          </h1>
        </div>

        {/* Upload */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-heading font-semibold mb-3">Hlaða upp skjali</h2>
          <p className="text-sm text-muted-foreground mb-4">Hlaðið upp leigusamningum, húsreglum eða öðrum skjölum sem þú getur sent á gesti.</p>
          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-neutral-300 rounded-lg p-8 cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
            <Upload className="w-6 h-6 text-muted-foreground" />
            <span className="text-sm font-ui text-muted-foreground">
              {uploading ? "Hleð upp..." : "Smelltu hér eða dragðu skjöl hingað"}
            </span>
            <Input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
              data-testid="document-upload-input"
            />
          </label>
          <p className="text-xs text-muted-foreground mt-2">Leyft: PDF, DOC, DOCX, XLS, XLSX, TXT, PNG, JPG</p>
        </div>

        {/* Documents List */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-lg font-heading font-semibold mb-4">Skráin þín ({documents.length})</h2>
          {documents.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Engin skjöl enn</p>
          ) : (
            <div className="space-y-2" data-testid="documents-list">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 bg-neutral-50 border border-neutral-100 rounded-lg px-4 py-3 group"
                  data-testid={`document-${doc.id}`}
                >
                  {getFileIcon(doc.original_name)}
                  <div className="flex-1 min-w-0">
                    <div className="font-ui text-sm font-medium truncate">{doc.original_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatBytes(doc.size)} &middot; {new Date(doc.created_at).toLocaleDateString("is-IS")}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <a
                      href={`${BACKEND_URL}${doc.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-neutral-400 hover:text-primary rounded transition-colors"
                      title="Sækja"
                      data-testid={`download-${doc.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => handleDelete(doc.id, doc.original_name)}
                      className="p-2 text-neutral-400 hover:text-red-600 rounded transition-colors"
                      title="Eyða"
                      data-testid={`delete-doc-${doc.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default DocumentsManagement;
